<template>
	<Accessible
		name="mediapreview"
		:tabOrder="[{ all: '.remove', clickOnEnter: true }]"
		class="media-container"
	>
		<MediaPreview
			:mediaItems="mediaItems"
			animate
			@remove="removeMediaItem"
			@reorder="reorderMediaItems"
			@click="onClickMediaItem"
		/>
	</Accessible>
	<div
		class="message-input-wrapper"
		:class="{
			'embedded-message-input-wrapper': store.config.embedded,
			disabled,
		}"
	>
		<div
			v-if="!showRecorder"
			class="textarea-container"
			:class="{
				'embedded-textarea-container': store.config.embedded,
				'wrapper-message-input-container': store.config.wrapper,
			}"
			ref="messageInputContainer"
		>
			<Editor
				v-focus
				v-model="message"
				ref="input"
				:placeholder="placeholder"
				:noEnter="isOverMax"
				:disabled="disabled"
				@focus="focus"
				@blur="blur"
				@keypress="textinput"
				@enterPressed="enterPressed"
				@inputRaw="setRawInput"
				@keyup.up="editLastMessage"
				@resolvingMentions="needsToResolveMentions = $event"
			/>

			<div class="message-input-right">
				<MessageCharLimit
					:text="message"
					@over="isOverMax = true"
					@under="isOverMax = false"
				/>
				<div
					v-if="!disabled"
					class="message-input-actions"
					v-bind:class="{
						'embedded-msg-input-btns': store.config.embedded,
					}"
				>
					<MicrophoneIcon
						v-if="showAudioMicrophone"
						type="regular"
						class="mic"
						@click="showRecorder = true"
					/>
					<div
						id="fileUploadBtn"
						class="btn-file-upload"
						:aria-label="$t('accessibility_upload_file')"
						role="button"
						@keyup.enter="$refs.fileInput.click()"
					>
						<label for="file-input" class="cursor">
							<PaperclipVerticalIcon class="paperclip" type="regular" />
						</label>

						<input
							v-if="!store.showNewTaskModal"
							id="file-input"
							type="file"
							ref="fileInput"
							multiple
							@change="onSelectFiles"
							@click="handleFileChange"
						/>
					</div>
					<EmojiPicker
						class="emojis"
						@change="addEmoji"
						role="button"
						:aria-label="$t('chat_add_emoji_alt')"
						:prerenderDelay="100"
					>
						<div class="emojis-icon">
							<FaceSmileIcon type="regular" />
						</div>
					</EmojiPicker>

					<GiphyPicker
						class="gifs"
						@change="onGif"
						role="button"
						:aria-label="$t('chat_add_gif_alt')"
					>
						<GifIcon class="gif-icon" type="regular" />
					</GiphyPicker>
				</div>
			</div>
		</div>

		<AudioRecorder
			v-if="showRecorder"
			class="recorder-container"
			autoStart
			@send="onSendAudio"
			@cancel="onCloseAudioRecorder"
			ref="audioRecorder"
		/>

		<DropZone @drop="onDropFiles" />
	</div>
</template>

<script>
import store, {
	getCurrentChat,
	getNonSystemMessages,
	getMyUserId,
	setMessage,
	updateMessage,
	getUser,
	getOrganization,
	getAllMessagesInThread,
	getUsersForBubble,
} from '@/store'
import moment from 'moment'
import { GiphyPicker, showAlertModal } from '@/components/common'
import {
	GifIcon,
	PaperclipVerticalIcon,
	FaceSmileIcon,
	MicrophoneIcon,
} from '@/icons'
import Editor, { ASYNC_MENTIONS_REGEXP } from '@/components/Editor'
import AudioRecorder from './AudioRecorder'
import { mediaTypes, MAX_CHARS, MAX_MESSAGE_MEDIA } from '@/helpers/constants'
import { uploadNew } from '@/api/upload'
import MessageCharLimit, { getStringBytes } from './MessageCharLimit'
import EmojiPicker from '@/components/common/EmojiPicker'
import { chatCan, CREATE_MESSAGE } from '@/helpers/permissions'
import DropZone from '@/components/Home/DropZone'

import MediaPreview from '@/components/MessageInput/MediaPreview'
import { normalizeGiphyMediaItem } from '@/helpers/utils'
import { deleteUpload, setUpload } from '@/store/uploads'
import { sendMessage, prepFilesForUpload } from '@/api/message'
import { copyNativeFiles } from '@/helpers/utils'
import Accessible from '@/components/common/Accessible'
import { createLogger } from '@/helpers/log'

const log = createLogger('MessageInput', true)

export default {
	props: ['parentId'],
	components: {
		GiphyPicker,
		EmojiPicker,
		GifIcon,
		PaperclipVerticalIcon,
		FaceSmileIcon,
		Editor,
		MicrophoneIcon,
		AudioRecorder,
		MessageCharLimit,
		MediaPreview,
		DropZone,
		Accessible,
	},

	data() {
		return {
			store,
			message: '',
			formattedMessage: '',
			showFail: false,
			isOverMax: false,
			showRecorder: false,
			showMicOverride: false,
			org: getOrganization() || {},
			needsToResolveMentions: false,
			mediaItems: [],
			tempUUID: crypto.randomUUID(),
		}
	},

	emits: [
		'keyup',
		'blur',
		'messageSent',
		'messageCreated',
		'sendFailed',
		'focus',
	],

	created() {
		this.$onEventBus({
			'org-update': () => {
				this.org = getOrganization() || {}
			},
			devToolsAllowAudio: (bool) => {
				this.showMicOverride = bool
			},
		})
		window.addEventListener('paste', this.onPasteFiles)
	},

	beforeUnmount() {
		this.resizeObserver?.disconnect()
		window.removeEventListener('paste', this.onPasteFiles)
	},

	watch: {
		message: function (val, oldVal) {
			if (val !== oldVal) {
				this.$emit('keyup')
			}
		},
		'store.currentChatId': {
			handler() {
				this.showRecorder = false
				this.mediaItems = []
			},
			deep: true,
		},
	},

	computed: {
		disabled() {
			const currentChat = getCurrentChat()
			if (currentChat) {
				if (currentChat.isdm) {
					if (currentChat.isMultiDM) {
						// Get all users except current user
						const otherUsers = getUsersForBubble(currentChat.id).filter(
							(u) => u.id !== getMyUserId()
						)

						// Only check deactivation if we have all other members loaded
						const expectedOtherMembers = currentChat.dm_memberships_count - 1
						if (
							otherUsers.length === expectedOtherMembers &&
							otherUsers.every((u) => u.deactivated_at !== null)
						) {
							return true
						}
						return false
					} else {
						return (
							currentChat.dmpartner_id &&
							getUser(currentChat.dmpartner_id)?.inactive
						)
					}
				} else {
					return !chatCan(currentChat, CREATE_MESSAGE)
				}
			}
			return false
		},

		placeholder() {
			const currentChat = getCurrentChat()
			if (currentChat?.isdm) {
				if (currentChat.isMultiDM) {
					// Get all users except current user
					const otherUsers = getUsersForBubble(currentChat.id).filter(
						(u) => u.id !== getMyUserId()
					)

					// Only check deactivation if we have all other members loaded
					const expectedOtherMembers = currentChat.dm_memberships_count - 1
					if (
						otherUsers.length === expectedOtherMembers &&
						otherUsers.every((u) => u.deactivated_at !== null)
					) {
						return this.$t('chat_users_deactivated')
					}
				} else if (
					currentChat?.dmpartner_id &&
					getUser(currentChat.dmpartner_id)?.inactive
				) {
					return this.$t('chat_user_deactivated')
				}
			}

			if (!chatCan(currentChat, CREATE_MESSAGE)) {
				return this.$t('chat_input_text_placeholder_permission')
			} else if (this.parentId) {
				return this.$t('chat_input_text_placeholder_reply')
			} else {
				return this.$t('chat_input_text_placeholder_mentions')
			}
		},

		messageLength() {
			return getStringBytes(this.message)
		},

		isValidMessage() {
			return (
				this.messageLength > 0 &&
				this.messageLength <= MAX_CHARS &&
				/\S/.test(this.message)
			)
		},

		showAudioMicrophone() {
			// TODO: Remove this override once transcoding is in place
			return this.showMicOverride
			// return (
			// 	this.org.audio_messages_enabled &&
			// 	!this.messageLength
			// )
		},
	},

	methods: {
		removeMediaItem(item) {
			log('user removed media', item.tempId)
			deleteUpload(item.tempId)
			// We have to remove the item in place using .splice() because SortableJS doesn't
			// like it when the original array gets replaced wholesale when using .filter()
			const index = this.mediaItems.findIndex((m) => m.tempId === item.tempId)
			this.mediaItems.splice(index, 1)
		},
		reorderMediaItems(orderedTempIds) {
			log('user reordered media', orderedTempIds)
			if (!this.mediaItems?.length) return
			this.mediaItems = orderedTempIds.map((tempId) =>
				this.mediaItems.find((m) => m.tempId === tempId)
			)
		},
		onClickMediaItem(item, items) {
			log('user opening media in fullscreen modal', item.tempId)
			this.$mediaItems(items, item.tempId)
		},
		editLastMessage() {
			if (this.message !== '' && this.message !== '\n') return
			const userId = getMyUserId()
			let messageToEdit = null

			if (this.parentId) {
				// we're in thread view so we need to get the right list of messages
				messageToEdit = getAllMessagesInThread(this.parentId)
					.filter((m) => !m.systemevent)
					.reverse()
					.find((m) => m.user_id === userId)
			} else {
				// otherwise, get main list of chat messages
				messageToEdit = getNonSystemMessages()
					.slice()
					.reverse()
					.find((m) => m.user_id === userId)
			}

			if (!messageToEdit) return

			EventBus.$emit('editLatestMessage', {
				messageId: messageToEdit.id,
				threadId: this.parentId,
			})
		},

		onGif(giphyData) {
			log('selected giphy image', giphyData)
			if (!giphyData || !giphyData.images) return
			if (this.mediaItems.length >= MAX_MESSAGE_MEDIA) {
				log('giphy exceeds max count', this.mediaItems.length)
				return showAlertModal({
					title: this.$t('chat_attachment_max_limit_exceeded_title'),
					description: this.$t(
						'chat_attachment_max_limit_exceeded_description',
						{ Count: MAX_MESSAGE_MEDIA }
					),
				})
			}
			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const mediaItem = {
				...normalizeGiphyMediaItem(giphyData),
				isComplete: true,
				isNormalized: true,
				messageUUID: this.tempUUID,
			}
			delete mediaItem.uuid
			setUpload(mediaItem.tempId, {
				tempId: mediaItem.tempId,
				mediatype: mediaItem.mediatype,
				messageUUID: mediaItem.messageUUID,
				uploadProgress: 100,
				isComplete: true,
				isNormalized: true,
			})
			this.mediaItems.push(mediaItem)
			this.setFocus()
		},

		setFocus() {
			EventBus.$emit('focus-editor')
		},

		focus() {
			this.$emit('focus')
		},

		blur() {
			this.$emit('blur')
		},

		textinput(e) {
			this.$emit('keyup', e)
		},

		enterPressed() {
			const hasText = this.messageLength > 0
			if (hasText && this.isValidMessage) {
				this.showFail = false
				this.sendMessage()
			} else if (!hasText && this.mediaItems.length > 0) {
				this.showFail = false
				this.sendMessage()
			}
		},

		generateMessagePayload(data = {}) {
			const payload = {
				id: null,
				uuid: this.tempUUID || crypto.randomUUID(),
				bubble_id: this.store.currentChatId,
				message: this.message.trim(),
				created_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
				sendState: 'waiting',
				user_id: getMyUserId(),
				messagemedia: [],
				...data,
			}

			if (this.parentId) {
				payload.parentmessage_id = this.parentId
			}

			if (this.audioMedia) {
				payload.messagemedia = this.audioMedia
				this.audioMedia = null
			}

			if (this.needsToResolveMentions) {
				const containsPotentialMention = ASYNC_MENTIONS_REGEXP.test(
					payload.message
				)
				payload.replace_string_mentions = containsPotentialMention
			}

			return payload
		},

		async sendMessage(messagePayload) {
			log('attempting to send message', messagePayload)
			this.$emit('messageSent')

			if (!messagePayload) {
				messagePayload = this.generateMessagePayload()
				setMessage(messagePayload)
			}

			this.$emit('messageCreated')
			this.message = ''
			this.tempUUID = null

			this.$refs.input.clear()

			if (this.mediaItems.length) {
				const incomplete = this.mediaItems.filter((m) => !m.isComplete)
				messagePayload.messagemedia = this.mediaItems
				setMessage(messagePayload)
				this.mediaItems = []

				// if we haven't finished uploading files, then we'll just skip out now
				if (incomplete.length) {
					log('uploads not complete, waiting...', incomplete)
					return
				}
			}

			try {
				await sendMessage(messagePayload)
			} catch (e) {}
		},

		handleFileChange(e) {
			e.target.value = ''
		},

		onSelectFiles(e) {
			log('user selected files from os picker', copyNativeFiles(e.target.files))
			this.onFileChange(e.target.files)
		},

		onDropFiles(e) {
			const { files } = e.dataTransfer || e.target || {}
			log('user dropped files', copyNativeFiles(files))
			this.onFileChange(files)
		},

		onPasteFiles(e) {
			if (!e.clipboardData?.items?.length) return
			log('user pasted file content', copyNativeFiles(e.clipboardData.items))
			const files = Array.from(e.clipboardData.items)
				.filter((item) => item.type?.includes('image/'))
				.map((item) => item.getAsFile())
			this.onFileChange(files)
		},

		async onFileChange(files = []) {
			if (!files?.length) return false

			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const normalizedMedia = await prepFilesForUpload(
				files,
				this.mediaItems.length,
				this.tempUUID
			)

			normalizedMedia.forEach((media) => {
				this.mediaItems.push(media)
			})

			this.$nextTick(() => this.setFocus())
		},

		addEmoji(emoji) {
			this.$refs.input.insertEmoji(emoji.emoji)
			this.setFocus()
		},

		setRawInput(data) {
			this.formattedMessage = data
		},

		async onSendAudio({ file, duration }) {
			this.showRecorder = false
			const messagemedia = [
				{
					title: file.name,
					mediatype: mediaTypes.AUDIO,
					filesize: file.size,
					duration,
					urlmimetype: file.type,
				},
			]
			this.audioMedia = messagemedia
			try {
				const message = this.generateMessagePayload()
				setMessage(message)
				const { data } = await uploadNew(file, (progressPct) => {
					messagemedia[0].uploadProgressPct = progressPct
					updateMessage(message.uuid, { messagemedia })
				})
				messagemedia[0].uuid = data.key
				updateMessage(message.uuid, { messagemedia })
				await this.sendMessage(message)
			} catch (e) {
				console.error('Error sending audio message', e)
				this.$alert.error(e.message || null)
			}
		},

		onCloseAudioRecorder() {
			this.showRecorder = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@media screen and (max-width: 800px) {
	:deep(.emoji-mart.picker) {
		bottom: 50px;
	}
}

.message-input-wrapper {
	border-bottom-left-radius: 10px;
	position: relative;
	&.disabled {
		pointer-events: none;
		user-select: none;
	}
}

.embedded-message-input-wrapper {
	border-bottom-left-radius: 0px;
}

:deep(.emoji-mart.picker) {
	max-height: calc(100vh - 130px);
}

.remove-top-border {
	border-top: none !important;
}

.emoji-shortcuts {
	position: absolute;
	bottom: 63px;
}

.pronto-editor {
	max-width: calc(100% - 110px);
	@media screen and (max-width: 800px) {
		width: 100%;
		max-width: 100%;
	}
}
.media-container {
	:deep(.media-preview-container) {
		&.animate {
			&.expanded {
				height: 114px;
				&.only-files {
					height: 82px;
				}
			}
		}
		.media-preview-items {
			padding: 24px 24px 0 32px;
		}
	}
}

.emojis {
	cursor: pointer;
	&:hover {
		color: var(--neutral-600);
	}
	&.open {
		color: var(--teal-300);
	}
}
.pronto-icon {
	cursor: pointer;
	color: var(--neutral-400);
	font-size: 24px;
	&:hover {
		color: var(--neutral-600);
	}
	&.open {
		color: var(--teal-300);
	}
	&.gif-icon {
		font-size: 30px;
		margin-top: -3px;
	}
	&.paperclip,
	&.mic {
		width: 18px;
	}
}

.message-input-right {
	@include flex-column-center;
	margin-bottom: 15px;
	row-gap: 16px;
}

.message-input-actions {
	@include flex;
	gap: 12px;
	@media screen and (max-width: 800px) {
		right: 10px !important;
	}
	> * {
		height: 24px;
	}
}

.textarea-container {
	position: relative;
	border: 0;
	width: 100%;
	background-color: var(--neutral-0);
	padding: 0 16px 0 30px;
	display: flex;
	align-items: flex-end;
	gap: 16px;
}
</style>
